import { EventData } from "../models/event";
import {
  ReviewParameterKey,
  ReviewSummary,
} from "../models/review";

export const REVIEW_PARAMETER_KEYS: ReviewParameterKey[] = [
  "registration_communication",
  "expo_experience",
  "event_experience",
  "merchandise_goodie_bag",
  "event_arrangements",
  "on_course_amenities",
  "event_entertainment",
  "medical_facilities",
  "medal_feedback",
];

const AVERAGE_SOURCE_KEYS = [
  "review_averages",
  "average_ratings",
  "average_rating",
  "review_average",
  "event_review_average",
  "review_summary",
] as const;

function readParameterAverage(
  source: Record<string, unknown>,
  key: ReviewParameterKey,
): string | undefined {
  const raw = source[key];
  if (raw === null || raw === undefined || raw === "") return undefined;
  const num = Number(raw);
  if (Number.isNaN(num)) return undefined;
  return String(num);
}

/** Normalize getAverageRating / nested API shapes into ReviewSummary. */
export function normalizeReviewSummary(
  data: Record<string, unknown> | null | undefined,
): ReviewSummary | null {
  if (!data || typeof data !== "object") return null;

  const nestedSources: Record<string, unknown>[] = [
    data,
    ...AVERAGE_SOURCE_KEYS.map((k) => data[k]).filter(
      (v): v is Record<string, unknown> =>
        typeof v === "object" && v !== null && !Array.isArray(v),
    ),
  ];

  if (
    typeof data.average_rating === "number" &&
    !REVIEW_PARAMETER_KEYS.some((key) => data[key] != null)
  ) {
    return null;
  }

  const partial: Partial<ReviewSummary> = {};
  let foundAny = false;

  for (const source of nestedSources) {
    for (const key of REVIEW_PARAMETER_KEYS) {
      if (partial[key] !== undefined) continue;
      const value = readParameterAverage(source, key);
      if (value !== undefined) {
        partial[key] = value;
        foundAny = true;
      }
    }

    if (partial.total_reviews === undefined && source.total_reviews != null) {
      partial.total_reviews = Number(source.total_reviews);
    }
  }

  if (!foundAny) return null;

  return {
    total_reviews: partial.total_reviews ?? 0,
    ...REVIEW_PARAMETER_KEYS.reduce(
      (acc, key) => {
        acc[key] = partial[key] ?? "0";
        return acc;
      },
      {} as Record<ReviewParameterKey, string>,
    ),
  };
}

/** Pull per-parameter averages from Event GET response (multiple backend shapes). */
export function extractEventReviewAverages(
  event: (EventData & Record<string, unknown>) | null | undefined,
): ReviewSummary | null {
  if (!event) return null;

  return normalizeReviewSummary(event as Record<string, unknown>);
}

export function getOverallAverageFromSummary(
  summary: ReviewSummary | null,
): number {
  if (!summary) return 0;

  const values = REVIEW_PARAMETER_KEYS.map((key) =>
    Number(summary[key] ?? 0),
  ).filter((v) => v > 0);

  if (values.length === 0) return 0;
  return parseFloat(
    (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1),
  );
}
